import { Box } from '@mui/material';
import { FormikHelpers } from 'formik';
import { FC, useCallback } from 'react';

import { adminValidationSchema } from 'src/schemas/admin';
import { useAlert } from '../../hooks/useAlert';
import { theme } from '../../services/theme';
import { admin, useUpdateWorkBetterAdminMutation } from '../../services/user';
import { Alert } from '../Alert';
import { Form } from '../forms/Form';
import { EditAdminFormContent } from './EditAdminFormContent';

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
}

interface Props {
  onCancel: () => void;
  onFailure?: () => void;
  onSuccess?: () => void;
  editAdmin: admin;
}

export const EditAdminForm: FC<Props> = ({
  onCancel,
  onSuccess,
  editAdmin,
}) => {
  const [updateWorkBetterAdmin] = useUpdateWorkBetterAdminMutation();

  const formInitialValues: FormValues = {
    email: editAdmin.email,
    firstName: editAdmin.firstName,
    lastName: editAdmin.lastName,
  };

  const { alert, isAlertOpen, onCloseAlert, onOpenAlert } = useAlert();

  const onSubmit = useCallback(
    async (
      values: FormValues,
      { setSubmitting }: FormikHelpers<FormValues>,
    ) => {
      setSubmitting(true);

      const finalObj: FormValues = {} as FormValues;
      if (formInitialValues?.email) {
        if (formInitialValues.email !== values.email)
          finalObj.email = values.email;
        if (formInitialValues.firstName !== values.firstName)
          finalObj.firstName = values.firstName;
        if (formInitialValues.lastName !== values.lastName)
          finalObj.lastName = values.lastName;
      }

      try {
        await updateWorkBetterAdmin({
          userId: editAdmin.id,
          ...finalObj,
        }).unwrap();

        onOpenAlert({
          message: 'WorkBetter admin has been successfully updated.',
          onClose: onSuccess,
          title: 'WorkBetter admin updated!',
          type: 'success',
        });
      } catch (err: any) {
        onOpenAlert({
          message: err?.data?.message === 'EMAIL_ALREADY_IN_USE'
            ? 'A user with this email address already exists.'
            : 'Update WorkBetter admin failed.',
          title: 'Oops!',
          type: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [editAdmin.id, formInitialValues],
  );

  return (
    <Box
      sx={{
        background: '#F1F6F9',
        boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.25)',
        borderRadius: '12px',
        padding: '31px 20px',
        [theme.breakpoints.down('md')]: {
          borderRadius: '10px',
          padding: '25px 10px',
        },
      }}
    >
      {formInitialValues && (
        <Form<FormValues>
          enableReinitialize={true}
          initialValues={formInitialValues}
          onSubmit={onSubmit}
          validationSchema={adminValidationSchema}
        >
          {({ values, setFieldValue }) => (
            <EditAdminFormContent
              values={values}
              setFieldValue={setFieldValue}
              onCancel={onCancel}
            />
          )}
        </Form>
      )}
      <Alert
        isOpen={isAlertOpen}
        message={alert.message}
        onClose={onCloseAlert}
        title={alert.title}
        type={alert.type}
      />
    </Box>
  );
};
